import { useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Image, FileText, Images, Inbox, ShoppingBag, Mail, Settings, LogOut, ExternalLink, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/kunstwerke", label: "Kunstwerke", icon: Image },
  { to: "/admin/gedichte", label: "Gedichte", icon: FileText },
  { to: "/admin/medien", label: "Medien", icon: Images },
  { to: "/admin/anfragen", label: "Anfragen", icon: Inbox },
  { to: "/admin/bestellanfragen", label: "Bestellanfragen", icon: ShoppingBag },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/einstellungen", label: "Einstellungen", icon: Settings },
];

function NavLinks({ onNavigate, light = false }) {
  const { logout } = useAuth();
  // light=false -> dark sidebar (helle Texte); light=true -> weißes Panel (dunkle Texte)
  const idle = light
    ? "text-[#32104F] hover:bg-[#F3E9FB] hover:text-[#32104F]"
    : "text-[#E9D8F5] hover:bg-[#632A8E]/60 hover:text-[#FFF9F2]";
  const divider = light ? "border-[#E5D5F0]" : "border-[#632A8E]/60";
  return (
    <nav className="flex flex-col gap-1">
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`admin-nav-${n.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm rounded-md transition-colors ${
              isActive ? "bg-[#F4C542] text-[#32104F] font-medium" : idle
            }`
          }
        >
          <n.icon className="w-[18px] h-[18px]" strokeWidth={1.5} /> {n.label}
        </NavLink>
      ))}
      <div className={`border-t ${divider} my-3`} />
      <a href="/" target="_blank" rel="noreferrer" className={`flex items-center gap-3 px-4 py-3 text-sm rounded-md ${idle}`}>
        <ExternalLink className="w-[18px] h-[18px]" strokeWidth={1.5} /> Website ansehen
      </a>
      <button onClick={logout} data-testid="admin-logout" className={`flex items-center gap-3 px-4 py-3 text-sm rounded-md text-left ${idle}`}>
        <LogOut className="w-[18px] h-[18px]" strokeWidth={1.5} /> Abmelden
      </button>
    </nav>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#FFF4D6] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#32104F] fixed inset-y-0">
        <Link to="/admin" className="flex items-center gap-2 px-5 py-5 border-b border-[#632A8E]/50">
          <Logo variant="butterfly" className="h-8 w-auto" />
          <span className="font-serif text-2xl tracking-[0.15em] text-[#FFF9F2]">GYSUMO</span>
        </Link>
        <div className="p-3 flex-1 overflow-y-auto"><NavLinks /></div>
      </aside>

      <div className="flex-1 md:ml-64">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-40 bg-[#32104F] flex items-center justify-between px-4 h-14">
          <Link to="/admin" className="flex items-center gap-2">
            <Logo variant="butterfly" className="h-7 w-auto" />
            <span className="font-serif text-xl tracking-[0.15em] text-[#FFF9F2]">GYSUMO</span>
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="admin-mobile-menu" aria-label="Menü" className="p-2 text-[#F4C542]"><Menu className="w-6 h-6" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-white w-72 p-0">
              <SheetTitle className="sr-only">Admin-Navigation</SheetTitle>
              <SheetDescription className="sr-only">Navigation für den Adminbereich</SheetDescription>
              <div className="flex items-center gap-2 bg-[#32104F] px-5 py-5">
                <Logo variant="butterfly" className="h-8 w-auto" />
                <span className="font-serif text-2xl tracking-[0.15em] text-[#FFF9F2]">GYSUMO</span>
              </div>
              <div className="p-3"><NavLinks light onNavigate={() => setOpen(false)} /></div>
            </SheetContent>
          </Sheet>
        </div>

        <main className="p-4 sm:p-8 max-w-6xl mx-auto" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
